import React from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import GoldShieldLogo from "@/components/GoldShieldLogo";

const PrivacyPolicy = () => (
  <div className="min-h-screen bg-slate-950 text-white flex flex-col">
    <SEO 
      title="Privacy Policy | RTS EXPRESS LTD"
      description="Privacy policy for RTS EXPRESS LTD. Learn how we collect, use and protect your personal information when using our courier services."
      path="/privacy-policy"
    />
    <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-md border-b border-slate-800">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="legal-nav-home-link">
          <GoldShieldLogo className="w-10 h-12" />
          <div className="flex flex-col">
            <span className="text-xl font-bold text-white tracking-tight leading-tight">RTS EXPRESS</span>
            <span className="text-[10px] text-slate-400 tracking-wide">SAME DAY COURIER</span>
          </div>
        </Link>
        <div className="hidden md:flex items-center gap-8">
          <a href="/#services" className="text-slate-400 hover:text-white transition-colors" data-testid="legal-nav-services-link">Services</a>
          <a href="/#how-it-works" className="text-slate-400 hover:text-white transition-colors" data-testid="legal-nav-how-link">How It Works</a>
          <a href="/#contact" className="text-slate-400 hover:text-white transition-colors" data-testid="legal-nav-contact-link">Contact</a>
        </div>
        <Link to="/get-quote" className="bg-green-500 text-slate-950 hover:bg-green-500/90 font-semibold px-6 py-2 rounded-sm transition-colors" data-testid="legal-get-quote-btn">
          Get a Quote
        </Link>
      </div>
    </nav>

    <main className="flex-1 pt-24 pb-16">
      <div className="max-w-4xl mx-auto px-6">
        <h1 className="text-4xl md:text-5xl font-bold mb-6" data-testid="legal-page-title">Privacy Policy</h1>
        <div className="text-slate-300 space-y-4" data-testid="legal-page-content">
          <p><strong>Last updated: 25 February 2026</strong></p>
          <p>
            RTS EXPRESS LTD (“we”, “us”) respects your privacy. This Privacy Policy explains how we collect, use and protect personal data when you use our website and services.
          </p>

          <h2 className="text-2xl font-semibold text-white">1. Who we are (Data Controller)</h2>
          <p>
            Data Controller: RTS EXPRESS LTD (Company No. 13973121)
            <br />
            Registered office / HQ: 1 Bradestone Road, Nuneaton, Warwickshire, CV11 4QR, United Kingdom
            <br />
            Email: info@rtsexpress.co.uk
          </p>

          <h2 className="text-2xl font-semibold text-white">2. Personal data we collect</h2>
          <p>Depending on how you use our website/services, we may collect:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Identity and contact details (name, business name, email, phone)</li>
            <li>Collection and delivery details (addresses, contact names, instructions)</li>
            <li>Shipment information (description, weights, dimensions, urgency, notes)</li>
            <li>Payment/transaction references (we do not store full card details; payments are processed by Stripe)</li>
            <li>Communications (emails/messages you send us)</li>
            <li>Technical data (IP address, device/browser data, cookies where applicable)</li>
            <li>Photographic evidence collected at collection and delivery locations for proof of loading, securing, and delivery of goods (which may incidentally include individuals, vehicle registration numbers, or premises)</li>
          </ul>

          <h2 className="text-2xl font-semibold text-white">3. How we use your data (purposes)</h2>
          <p>We use your data to:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>process quote requests and communicate quotations</li>
            <li>arrange, perform and manage courier services</li>
            <li>process payments and issue invoices/receipts</li>
            <li>handle customer service, complaints and claims</li>
            <li>prevent fraud and keep the website secure</li>
            <li>comply with legal obligations (tax/accounting, regulatory requirements)</li>
          </ul>

          <h2 className="text-2xl font-semibold text-white">4. Lawful bases (UK GDPR)</h2>
          <p>We process personal data under one or more lawful bases:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Contract: to provide quotes/services you request and manage bookings</li>
            <li>Legitimate interests: to run and improve our business, prevent fraud, keep records</li>
            <li>Legal obligation: tax/accounting and compliance requirements</li>
            <li>Consent: where required for non-essential cookies or marketing communications</li>
          </ul>
          <p>
            Where we rely on legitimate interests, we carefully assess and balance our business interests against your data protection rights and ensure such interests do not override your fundamental rights and freedoms.
          </p>

          <h2 className="text-2xl font-semibold text-white">5. Sharing your data</h2>
          <p>We may share data with trusted service providers where necessary, such as:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Payment processing: Stripe</li>
            <li>Email and business tools: Google Workspace</li>
            <li>Website/app hosting and operations: our hosting/platform providers (including Emergent and related infrastructure)</li>
            <li>Professional advisers (accountants, insurers, legal advisers) where necessary</li>
            <li>Authorities where required by law</li>
          </ul>
          <p>We do not sell personal data.</p>

          <h2 className="text-2xl font-semibold text-white">6. International transfers</h2>
          <p>
            Some providers may process data outside the UK. Where international transfers occur, we use appropriate safeguards (such as standard contractual clauses or other lawful mechanisms) as required.
          </p>

          <h2 className="text-2xl font-semibold text-white">7. Data retention</h2>
          <p>
            We retain personal data only for as long as necessary to fulfil the purposes for which it was collected, including legal, accounting, insurance, and dispute resolution requirements.
          </p>
          <p>Retention periods are as follows:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Booking records, contracts, invoices, and payment records: retained for 6 years from the end of the relevant financial year (in accordance with UK accounting and HMRC requirements).</li>
            <li>Proof of Delivery (POD) documents, timestamps, signed confirmations, and photographic evidence: retained for 6 years for contractual and legal defence purposes.</li>
            <li>Customer communications relating to bookings or services: retained for 6 years.</li>
            <li>Unsuccessful quotation enquiries: retained for up to 12 months.</li>
            <li>Marketing data (where applicable): retained until consent is withdrawn.</li>
          </ul>
          <p>
            After the applicable retention period expires, data is securely deleted or anonymised.
          </p>

          <h2 className="text-2xl font-semibold text-white">8. Security</h2>
          <p>
            We use reasonable technical and organisational measures to protect data. No system is 100% secure; please use caution when sending sensitive information.
          </p>

          <h2 className="text-2xl font-semibold text-white">9. Your rights</h2>
          <p>Subject to UK GDPR, you may have rights to:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>access your personal data</li>
            <li>correct inaccurate data</li>
            <li>request deletion (where applicable)</li>
            <li>restrict or object to processing</li>
            <li>data portability (where applicable)</li>
            <li>withdraw consent (where processing is based on consent)</li>
          </ul>
          <p>To exercise rights, email info@rtsexpress.co.uk.</p>

          <h2 className="text-2xl font-semibold text-white">10. Complaints</h2>
          <p>
            If you are unhappy, you can complain to us first. You also have the right to complain to the UK Information Commissioner’s Office (ICO).
          </p>

          <h2 className="text-2xl font-semibold text-white">11. Cookies</h2>
          <p>
            We use cookies as described in our Cookie Policy. Where required, we will ask for your consent for non-essential cookies.
          </p>

          <h2 className="text-2xl font-semibold text-white">12. Changes</h2>
          <p>
            We may update this Privacy Policy from time to time. The “Last updated” date will show the latest version.
          </p>

          <h2 className="text-2xl font-semibold text-white">13. Data Protection Registration</h2>
          <p>
            RTS EXPRESS LTD is registered as a data controller with the UK Information Commissioner's Office (ICO).
            <br />
            Registration reference: <strong>ZC095659</strong>
            <br />
            We comply with the UK GDPR and the Data Protection Act 2018.
          </p>


        <p>
          Data Protection Contact: gdpr@rtsexpress.co.uk
        </p>
        </div>
      </div>
    </main>

    <footer className="border-t border-slate-800 bg-slate-950">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-sm" data-testid="legal-footer-copyright">
            Copyright {new Date().getFullYear()} RTS EXPRESS LTD. All rights reserved.
          </p>
          <div className="flex items-center gap-4 text-xs text-slate-500">
            <span>Company No: 13973121</span>
            <span className="text-slate-700">|</span>
            <span>VAT No: GB414935787</span>
            <span className="text-slate-700">|</span>
            <span>ICO: ZC095659</span>
            <span className="text-slate-700">|</span>
            <span>Payment required before collection</span>
          </div>
        </div>
        <p className="mt-2 text-slate-500 text-sm" data-testid="legal-footer-email">
          info@rtsexpress.co.uk
        </p>
        <div className="mt-4 flex flex-wrap gap-4 text-sm">
          <Link to="/terms-and-conditions" className="text-slate-400 hover:text-white transition-colors" data-testid="legal-footer-terms-link">
            Terms &amp; Conditions
          </Link>
          <Link to="/privacy-policy" className="text-slate-400 hover:text-white transition-colors" data-testid="legal-footer-privacy-link">
            Privacy Policy
          </Link>
          <Link to="/cookie-policy" className="text-slate-400 hover:text-white transition-colors" data-testid="legal-footer-cookie-link">
            Cookie Policy
          </Link>
          <button
            type="button"
            onClick={() => window.dispatchEvent(new Event("open-cookie-settings"))}
            className="text-slate-400 hover:text-white transition-colors"
            data-testid="legal-footer-cookie-settings-button"
          >
            Cookie Settings
          </button>
          <Link to="/website-disclaimer" className="text-slate-400 hover:text-white transition-colors" data-testid="legal-footer-disclaimer-link">
            Website Disclaimer
          </Link>
        </div>
      </div>
    </footer>
  </div>
);

export default PrivacyPolicy;